import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit, Trash2, DollarSign, Calendar, Check, X, Eye, EyeOff, Star, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config';

const SubscriptionPlans = ({ token, i18n }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/subscription-plans`, { headers });
      setPlans(res.data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في جلب البيانات' : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = () => {
    setEditingPlan({
      name: '',
      nameAr: '',
      description: '',
      descriptionAr: '',
      price: 0,
      currency: 'sek',
      duration: 30,
      durationType: 'days',
      features: [],
      featuresAr: [],
      isActive: true,
      isPopular: false
    });
    setModalOpen(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan({ ...plan });
    setModalOpen(true);
  };

  const handleSavePlan = async () => {
    if (!editingPlan.name.trim()) {
      toast.error(i18n.language === 'ar' ? 'الرجاء إدخال اسم الخطة' : 'Please enter plan name');
      return;
    }

    if (editingPlan.price < 0) {
      toast.error(i18n.language === 'ar' ? 'السعر يجب أن يكون صفر أو أكثر' : 'Price must be zero or more');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Always set currency to sek (Swedish Krona)
      const planData = { ...editingPlan, currency: 'sek' };
      
      if (editingPlan.id) {
        // Update
        await axios.put(`${API}/admin/subscription-plans/${editingPlan.id}`, planData, { headers });
        toast.success(i18n.language === 'ar' ? 'تم تحديث الخطة! ✅' : 'Plan updated! ✅');
      } else {
        // Create
        await axios.post(`${API}/admin/subscription-plans`, planData, { headers });
        toast.success(i18n.language === 'ar' ? 'تم إنشاء الخطة! ✅' : 'Plan created! ✅');
      }

      fetchPlans();
      setModalOpen(false);
      setEditingPlan(null);
    } catch (error) {
      console.error('Error saving plan:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(i18n.language === 'ar' ? `خطأ: ${errorMsg}` : `Error: ${errorMsg}`);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm(i18n.language === 'ar' ? 'هل أنت متأكد من حذف هذه الخطة؟' : 'Are you sure you want to delete this plan?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/subscription-plans/${planId}`, { headers });
      toast.success(i18n.language === 'ar' ? 'تم حذف الخطة' : 'Plan deleted');
      fetchPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في حذف الخطة' : 'Error deleting plan');
    }
  };

  const addFeature = (lang = 'en') => {
    const key = lang === 'ar' ? 'featuresAr' : 'features';
    setEditingPlan({
      ...editingPlan,
      [key]: [...(editingPlan[key] || []), '']
    });
  };

  const updateFeature = (index, value, lang = 'en') => {
    const key = lang === 'ar' ? 'featuresAr' : 'features';
    const features = [...(editingPlan[key] || [])];
    features[index] = value;
    setEditingPlan({ ...editingPlan, [key]: features });
  };

  const removeFeature = (index, lang = 'en') => {
    const key = lang === 'ar' ? 'featuresAr' : 'features';
    const features = [...(editingPlan[key] || [])];
    features.splice(index, 1);
    setEditingPlan({ ...editingPlan, [key]: features });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Package className="w-7 h-7 text-purple-600" />
              {i18n.language === 'ar' ? 'خطط الاشتراك' : 'Subscription Plans'}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {i18n.language === 'ar' 
                ? `${plans.length} خطة متاحة`
                : `${plans.length} plans available`
              }
            </p>
          </div>
          <Button
            onClick={handleCreatePlan}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            <Plus className="w-4 h-4 me-2" />
            {i18n.language === 'ar' ? 'إضافة خطة جديدة' : 'Add New Plan'}
          </Button>
        </div>

        {/* Toggle Preview Button */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="gap-2"
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showPreview 
              ? (i18n.language === 'ar' ? 'إخفاء المعاينة' : 'Hide Preview')
              : (i18n.language === 'ar' ? 'معاينة كما تظهر للمستخدمين' : 'Preview as Users See')
            }
          </Button>
        </div>

        {/* User Preview Section */}
        {showPreview && (
          <div className="mb-8 p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border-2 border-dashed border-slate-300">
            <h4 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {i18n.language === 'ar' ? 'معاينة البطاقات للمستخدمين' : 'User Preview'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.filter(p => p.isActive !== false).map((plan, index) => {
                const isPopular = plan.isPopular;
                const isFree = plan.price === 0;
                const isLastCard = index === plans.filter(p => p.isActive !== false).length - 1;
                const lang = i18n.language;
                
                const features = lang === 'ar' ? (plan.featuresAr || plan.features) : plan.features;
                const planName = lang === 'ar' ? (plan.nameAr || plan.name) : plan.name;
                
                // Duration text
                let durationText = '';
                if (plan.durationType === 'days') {
                  durationText = `${plan.duration} ${lang === 'ar' ? 'يوم' : 'days'}`;
                } else if (plan.durationType === 'year' || plan.durationType === 'years') {
                  durationText = lang === 'ar' ? '/سنة' : '/year';
                } else if (plan.durationType === 'months') {
                  if (plan.duration === 1) {
                    durationText = lang === 'ar' ? '/شهر' : '/month';
                  } else if (plan.duration === 6) {
                    durationText = lang === 'ar' ? '/6 أشهر' : '/6 months';
                  } else {
                    durationText = `/${plan.duration} ${lang === 'ar' ? 'أشهر' : 'months'}`;
                  }
                }

                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-3xl p-6 transition-all duration-300 overflow-hidden h-full flex flex-col ${
                      isLastCard
                        ? 'bg-gradient-to-br from-indigo-400 via-purple-400 to-pink-400 shadow-xl'
                        : isPopular
                        ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-xl'
                        : isFree
                        ? 'bg-white border-2 border-slate-200'
                        : 'bg-white border-2 border-emerald-200'
                    }`}
                  >
                    {/* Badge Area - Fixed height for alignment */}
                    <div className="h-8 flex items-center justify-center mb-2">
                      {isPopular && (
                        <div className="bg-amber-400 text-slate-900 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-md">
                          <Star className="w-4 h-4 fill-slate-900" />
                          <span>{lang === 'ar' ? 'الأشهر' : 'Popular'}</span>
                        </div>
                      )}
                    </div>

                    {/* Plan Details */}
                    <div className="text-center mb-6">
                      <div className="h-8 flex items-center justify-center">
                        <h3 className={`text-xl font-bold ${isLastCard || isPopular ? 'text-white' : 'text-slate-900'}`}>
                          {isFree ? (lang === 'ar' ? 'مجاني' : 'Free') : planName}
                        </h3>
                      </div>
                      
                      <div className="flex items-baseline justify-center gap-0.5 my-2">
                        <span className={`text-5xl font-black ${isLastCard || isPopular ? 'text-white' : 'text-slate-900'}`}>
                          {isFree ? '0' : plan.price}
                        </span>
                        <span className={`text-2xl font-bold ml-1 ${isLastCard ? 'text-purple-200' : isPopular ? 'text-emerald-200' : 'text-slate-400'}`}>
                          kr
                        </span>
                      </div>
                      
                      <p className={`text-xs font-medium ${isLastCard ? 'text-purple-200' : isPopular ? 'text-emerald-200' : 'text-slate-500'}`}>
                        {durationText || '/month'}
                      </p>
                    </div>

                    {/* Features List */}
                    <ul className="space-y-3 mb-6 flex-grow">
                      {features?.slice(0, 5).map((feature, idx) => (
                        <li key={idx} className={`flex items-start gap-2 text-sm ${isLastCard ? 'text-white' : isPopular ? 'text-emerald-50' : 'text-slate-600'}`}>
                          <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isLastCard ? 'text-purple-200' : isPopular ? 'text-emerald-200' : 'text-emerald-500'}`} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Button */}
                    <Button
                      disabled
                      className={`w-full rounded-full py-5 font-bold mt-auto ${
                        isLastCard
                          ? 'bg-white text-purple-600'
                          : isPopular
                          ? 'bg-white text-emerald-600'
                          : isFree
                          ? 'bg-slate-900 text-white'
                          : 'bg-emerald-500 text-white'
                      }`}
                    >
                      {isFree 
                        ? (lang === 'ar' ? 'ابدأ مجاناً' : 'Start Free')
                        : (lang === 'ar' ? 'اشترك الآن' : 'Subscribe')
                      }
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div 
              key={plan.id} 
              className={`relative bg-gradient-to-br ${
                plan.isPopular 
                  ? 'from-purple-50 to-pink-50 border-purple-300' 
                  : 'from-slate-50 to-slate-100 border-slate-200'
              } rounded-xl p-6 border-2 hover:shadow-lg transition-all`}
            >
              {plan.isPopular && (
                <div className="absolute -top-3 right-4">
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                    ⭐ {i18n.language === 'ar' ? 'الأشهر' : 'Popular'}
                  </Badge>
                </div>
              )}

              <div className="mb-4">
                <h4 className="text-xl font-bold text-slate-900 mb-1">
                  {i18n.language === 'ar' ? (plan.nameAr || plan.name) : plan.name}
                </h4>
                <p className="text-sm text-slate-600">
                  {i18n.language === 'ar' ? (plan.descriptionAr || plan.description) : plan.description}
                </p>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-purple-600">
                    {plan.price === 0 ? (i18n.language === 'ar' ? 'مجاني' : 'Free') : plan.price}
                  </span>
                  {plan.price > 0 && (
                    <>
                      <span className="text-lg text-slate-500">{plan.currency}</span>
                      <span className="text-sm text-slate-500">
                        / {plan.duration} {plan.durationType === 'days' 
                          ? (i18n.language === 'ar' ? 'يوم' : 'days')
                          : plan.durationType === 'months'
                          ? (i18n.language === 'ar' ? 'شهر' : 'months')
                          : (i18n.language === 'ar' ? 'سنة' : 'years')
                        }
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="space-y-2 mb-4">
                {(i18n.language === 'ar' ? plan.featuresAr : plan.features)?.slice(0, 4).map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </div>
                ))}
                {(plan.features?.length > 4) && (
                  <div className="text-xs text-slate-500 italic">
                    +{plan.features.length - 4} {i18n.language === 'ar' ? 'المزيد' : 'more'}
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="mb-4">
                <Badge className={plan.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {plan.isActive 
                    ? (i18n.language === 'ar' ? 'نشطة' : 'Active')
                    : (i18n.language === 'ar' ? 'معطلة' : 'Inactive')
                  }
                </Badge>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t border-slate-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditPlan(plan)}
                  className="flex-1 hover:bg-blue-50"
                >
                  <Edit className="w-4 h-4 me-1" />
                  {i18n.language === 'ar' ? 'تعديل' : 'Edit'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeletePlan(plan.id)}
                  className="flex-1 hover:bg-red-50 text-red-600"
                >
                  <Trash2 className="w-4 h-4 me-1" />
                  {i18n.language === 'ar' ? 'حذف' : 'Delete'}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {plans.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>{i18n.language === 'ar' ? 'لا توجد خطط اشتراك. أنشئ خطتك الأولى!' : 'No subscription plans yet. Create your first plan!'}</p>
          </div>
        )}
      </div>

      {/* Create/Edit Plan Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-6 h-6 text-purple-600" />
              {editingPlan?.id 
                ? (i18n.language === 'ar' ? 'تعديل الخطة' : 'Edit Plan')
                : (i18n.language === 'ar' ? 'إنشاء خطة جديدة' : 'Create New Plan')
              }
            </DialogTitle>
          </DialogHeader>

          {editingPlan && (
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{i18n.language === 'ar' ? 'اسم الخطة (EN)' : 'Plan Name (EN)'} *</Label>
                  <Input
                    value={editingPlan.name}
                    onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                    placeholder="Monthly Plan"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>{i18n.language === 'ar' ? 'اسم الخطة (AR)' : 'Plan Name (AR)'}</Label>
                  <Input
                    value={editingPlan.nameAr || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, nameAr: e.target.value })}
                    placeholder="الخطة الشهرية"
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{i18n.language === 'ar' ? 'الوصف (EN)' : 'Description (EN)'}</Label>
                  <Textarea
                    value={editingPlan.description || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })}
                    placeholder="Best for monthly users"
                    className="mt-2"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>{i18n.language === 'ar' ? 'الوصف (AR)' : 'Description (AR)'}</Label>
                  <Textarea
                    value={editingPlan.descriptionAr || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, descriptionAr: e.target.value })}
                    placeholder="الأفضل للمستخدمين الشهريين"
                    className="mt-2"
                    rows={2}
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                <h5 className="font-semibold text-slate-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  {i18n.language === 'ar' ? 'التسعير' : 'Pricing'}
                </h5>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>{i18n.language === 'ar' ? 'السعر' : 'Price'}</Label>
                    <Input
                      type="number"
                      value={editingPlan.price}
                      onChange={(e) => setEditingPlan({ ...editingPlan, price: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label>{i18n.language === 'ar' ? 'العملة' : 'Currency'}</Label>
                    <Input
                      value="sek"
                      readOnly
                      disabled
                      className="mt-2 bg-slate-100"
                    />
                  </div>

                  <div>
                    <Label>{i18n.language === 'ar' ? 'المدة' : 'Duration'}</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        type="number"
                        value={editingPlan.duration}
                        onChange={(e) => setEditingPlan({ ...editingPlan, duration: parseInt(e.target.value) || 1 })}
                        min="1"
                        className="flex-1"
                      />
                      <select
                        value={editingPlan.durationType}
                        onChange={(e) => setEditingPlan({ ...editingPlan, durationType: e.target.value })}
                        className="border rounded px-3 bg-white"
                      >
                        <option value="days">{i18n.language === 'ar' ? 'يوم' : 'Days'}</option>
                        <option value="months">{i18n.language === 'ar' ? 'شهر' : 'Months'}</option>
                        <option value="years">{i18n.language === 'ar' ? 'سنة' : 'Years'}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h5 className="font-semibold text-slate-900 mb-3">
                  {i18n.language === 'ar' ? 'المميزات (EN)' : 'Features (EN)'}
                </h5>
                <div className="space-y-2">
                  {editingPlan.features?.map((feature, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => updateFeature(idx, e.target.value, 'en')}
                        placeholder="Unlimited access"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeFeature(idx, 'en')}
                        className="text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addFeature('en')}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 me-2" />
                    {i18n.language === 'ar' ? 'إضافة ميزة' : 'Add Feature'}
                  </Button>
                </div>
              </div>

              <div>
                <h5 className="font-semibold text-slate-900 mb-3">
                  {i18n.language === 'ar' ? 'المميزات (AR)' : 'Features (AR)'}
                </h5>
                <div className="space-y-2">
                  {editingPlan.featuresAr?.map((feature, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => updateFeature(idx, e.target.value, 'ar')}
                        placeholder="وصول غير محدود"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeFeature(idx, 'ar')}
                        className="text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addFeature('ar')}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 me-2" />
                    {i18n.language === 'ar' ? 'إضافة ميزة' : 'Add Feature'}
                  </Button>
                </div>
              </div>

              {/* Status & Popular */}
              <div className="space-y-3 border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-900">{i18n.language === 'ar' ? 'الخطة نشطة' : 'Plan Active'}</div>
                    <div className="text-sm text-slate-500">{i18n.language === 'ar' ? 'متاحة للمستخدمين' : 'Available to users'}</div>
                  </div>
                  <Switch
                    checked={editingPlan.isActive}
                    onCheckedChange={(val) => setEditingPlan({ ...editingPlan, isActive: val })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-900">{i18n.language === 'ar' ? 'خطة شائعة' : 'Popular Plan'}</div>
                    <div className="text-sm text-slate-500">{i18n.language === 'ar' ? 'تظهر بشارة "الأشهر"' : 'Show "Popular" badge'}</div>
                  </div>
                  <Switch
                    checked={editingPlan.isPopular}
                    onCheckedChange={(val) => setEditingPlan({ ...editingPlan, isPopular: val })}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSavePlan}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              {i18n.language === 'ar' ? 'حفظ الخطة' : 'Save Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPlans;
